import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, SearchCheck, ShoppingCart, FileText, FileSearch, MonitorSmartphone, TrendingUp, Building2, Truck, Scale, HardHat, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader, SectionHeader, RichSection } from '../components/Shared';
import { PositionBox, FaqList } from '../components/FactCheck';
import { NextStepBand } from '../components/NextStepBand';

const REVIEW_AREAS = [
  { icon: ShoppingCart, label: 'Procurement' },
  { icon: FileText, label: 'Contracts' },
  { icon: FileSearch, label: 'Consultants' },
  { icon: MonitorSmartphone, label: 'Technology spending' },
  { icon: TrendingUp, label: 'Project overruns' },
  { icon: Building2, label: 'Management structure' },
  { icon: Truck, label: 'Fleet' },
  { icon: Scale, label: 'Legal expenses' },
  { icon: HardHat, label: 'Major capital projects' },
  { icon: ShieldCheck, label: 'Internal controls' },
];

const STEPS = [
  { n: '1', title: 'What is it?', body: 'A forensic audit is a deep, evidence-based examination of financial records, contracts, and processes by specialized independent professionals. It goes further than the regular annual audit: it follows the paper trail and tests whether controls actually worked.' },
  { n: '2', title: 'Why is Steve calling for one?', body: 'Because taxpayers deserve certainty, not assumptions. Years of growing costs, large projects, and complex contracts deserve one thorough, independent look. If everything is in order, residents gain confidence. If it is not, residents deserve to know.' },
  { n: '3', title: 'What should it examine?', body: 'The highest-risk, highest-dollar areas: procurement, contracts, consultants, technology spending, project overruns, management structure, fleet, legal expenses, major capital projects, and internal controls.' },
  { n: '4', title: 'Who should conduct it?', body: 'A qualified independent firm with forensic accounting credentials, selected through an open competitive process, with no prior relationship that could compromise independence.' },
  { n: '5', title: 'How should findings be reported?', body: 'Publicly. A written report to Council, published for residents in plain language, with management responses and a tracked action plan so recommendations do not gather dust.' },
];

const FAQS = [
  { q: 'Is Steve saying someone committed a crime?', a: 'No. Calling for a forensic audit does not imply criminal conduct by anyone, and no such claim is being made. It is about independent financial scrutiny and public accountability: verifying that systems, contracts, and controls work the way taxpayers are told they do.' },
  { q: 'Does the City not already get audited?', a: 'The City\'s annual financial statements are audited, which mainly confirms the statements fairly present the finances. A forensic audit is different: it digs into specific transactions, contracts, and controls to test how money was actually spent and managed.' },
  { q: 'What would it cost, and is it worth it?', a: 'An independent audit has a real cost, which is why its scope should target the highest-risk, highest-dollar areas first. If it finds savings, fixes weak controls, or simply gives residents verified confidence, it earns its keep.' },
  { q: 'What happens with the results?', a: 'Steve\'s position is that the full findings belong to the public: report to Council, publish in plain language, require management responses, and track every recommendation until it is closed.' },
];

export default function ForensicAudit() {
  return (
    <div>
      <PageHeader
        pageKey="forensic-audit"
        kicker="MY PLATFORM"
        title="I SUPPORT AN INDEPENDENT FORENSIC AUDIT."
        subtitle="Independent financial scrutiny and public accountability: verify how the money is really spent, and publish what is found."
      />
      <RichSection id="forensic-sections.intro" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />

      {/* The five questions, in order */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="audit-steps-section">
        <SectionHeader kicker="IN PLAIN LANGUAGE" title="The whole idea in five answers" />
        <div className="space-y-3">
          {STEPS.map((s) => (
            <Card key={s.n} data-testid={`audit-step-${s.n}`}>
              <CardContent className="p-5 sm:p-6 flex gap-4">
                <span className="inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-full bg-[#1a2744] text-[#C9A84C] font-black">{s.n}</span>
                <div>
                  <p className="font-serif font-bold text-[#1a2744] leading-snug">{s.title}</p>
                  <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">{s.body}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Potential review areas */}
        <div className="mt-10">
          <SectionHeader kicker="POTENTIAL SCOPE" title="Areas the audit could review" />
          <div className="flex flex-wrap gap-2.5" data-testid="audit-areas-list">
            {REVIEW_AREAS.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-bold text-[#1a2744]" data-testid="audit-area">
                <Icon className="h-4 w-4 text-[#8a6f22]" /> {label}
              </span>
            ))}
          </div>
        </div>

        {/* Important care statement */}
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5" data-testid="audit-care-note">
          <p className="text-sm text-slate-700 leading-relaxed"><span className="font-black text-[#1a2744]">Important:</span> calling for a forensic audit does not imply criminal conduct by anyone, and no such claim is made or supported here. This is about independent financial scrutiny and public accountability.</p>
        </div>

        <div className="mt-6">
          <PositionBox title="Verify it. Publish it. Fix it." testId="audit-position-band">
            <p>Taxpayers should never have to take "trust us" for an answer on hundreds of millions of dollars. One serious, independent, published review would either confirm confidence or show exactly what to fix. Either outcome serves the public.</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <Button asChild className="h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="audit-savings-button"><Link to="/savings-tracker">See the Savings Tracker <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              <Button asChild variant="outline" className="h-11 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press" data-testid="audit-finances-button"><Link to="/city-finances">City Finances, Explained</Link></Button>
            </div>
          </PositionBox>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="audit-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="PLAIN ANSWERS" title="Common questions" />
          <FaqList items={FAQS} testId="audit-faq" />
        </div>
      </section>

      {/* One clear next step */}
      <div className="pt-10">
        <NextStepBand
          title="See what Steve is already examining, in the open."
          buttonLabel="Open the Savings Tracker"
          to="/savings-tracker"
          secondaryLabel="Or read City Finances, Explained"
          secondaryTo="/city-finances"
          testId="audit-next-step"
        />
      </div>
    </div>
  );
}
